import { Classes, PanelProps } from '@blueprintjs/core';
import * as React from 'react';

import { TEST_IDS } from 'src/constants/testIds';
import {
  useDefaultModelAliasInfo,
  usePublish,
  useValidateModelName,
} from 'src/containers/PublishProjectDrawer/hooks';
import {
  MappingConfigPanel,
  MappingConfigPanelProps,
} from 'src/containers/PublishProjectDrawer/MappingConfigPanel';
import { ModelAliasesForm } from 'src/containers/PublishProjectDrawer/ModelAliasesForm';
import {
  OpenInExplorerPanel,
  OpenInExplorerPanelProps,
} from 'src/containers/PublishProjectDrawer/OpenInExplorerPanel';
import { PublishProjectDrawerFooter } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerFooter';
import { PublishProjectDrawerStepper } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerStepper';
import {
  CreationType,
  DatabaseInfo,
  ModelAliasInfo,
  ModelsInfo,
  RulesInfo,
  SensitivePropertyGroupsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { hasValidMappingsSelector } from 'src/selectors/hasValidMappings';
import { ModelSelectItem } from 'src/selectors/modelItems';
import { projectPublishHistorySelector } from 'src/selectors/projectPublishHistory';
import { replaceableModelItemsSelector } from 'src/selectors/replaceableModelItems';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export type ModelAliasConfigPanelProps = {
  databaseInfo: DatabaseInfo;
  isConsolidatingModels: boolean;
  isPublishingRules: boolean;
  modelsInfo: ModelsInfo;
  ruleInfo: RulesInfo;
  spgInfo: SensitivePropertyGroupsInfo;
};

export const ModelAliasConfigPanel: React.VFC<
  PanelProps<ModelAliasConfigPanelProps>
> = ({
  closePanel,
  databaseInfo,
  isConsolidatingModels,
  isPublishingRules,
  modelsInfo,
  ruleInfo,
  spgInfo,
  openPanel,
}) => {
  const { databaseId } = databaseInfo;
  const { projectId } = useRouterParams();
  const hasValidMappings = useReduxSelector(
    hasValidMappingsSelector(projectId)
  );
  const modelItems = useReduxSelector(
    replaceableModelItemsSelector(databaseId)
  );
  const publishHistory = useReduxSelector(
    projectPublishHistorySelector(projectId)
  );

  const defaultModelAliasInfo = useDefaultModelAliasInfo(
    databaseId,
    modelsInfo
  );
  const publish = usePublish();

  const isUsingHistoryAlias =
    Boolean(publishHistory?.modelAliasHistory) &&
    defaultModelAliasInfo.creationType === CreationType.REPLACE;
  const [localModelAliasInfo, setLocalModelAliasInfo] =
    React.useState<ModelAliasInfo>(defaultModelAliasInfo);
  const [existingAliasModel, setExistingAliasModel] =
    React.useState<ModelSelectItem | null>(
      isUsingHistoryAlias
        ? modelItems.find(
            (modelItem) => modelItem.value === defaultModelAliasInfo.modelName
          ) || null
        : null
    );
  const [isUsingAlias, setIsUsingAlias] = React.useState(isUsingHistoryAlias);

  const validateModelName = useValidateModelName({
    databaseId,
    isAlias: true,
    modelsInfo,
  });

  const nextButtonError = React.useMemo(() => {
    if (!isUsingAlias) {
      return '';
    }

    const modelNameError = validateModelName(localModelAliasInfo);
    if (modelNameError) {
      return modelNameError;
    }

    if (!localModelAliasInfo.namedGraphIris.length) {
      return copy.components.publishProjectDrawer.validation.noModelsSelected;
    }

    return '';
  }, [isUsingAlias, localModelAliasInfo, validateModelName]);

  const handleNext = React.useCallback(() => {
    if (nextButtonError) {
      return;
    }

    const modelAliasInfo =
      isUsingAlias && localModelAliasInfo.namedGraphIris.length > 0
        ? localModelAliasInfo
        : undefined;

    if (hasValidMappings) {
      const props: MappingConfigPanelProps = {
        databaseInfo,
        isConsolidatingModels,
        isPublishingRules,
        modelAliasInfo,
        modelsInfo,
        ruleInfo,
        spgInfo,
      };
      openPanel({ renderPanel: MappingConfigPanel, props });
    } else {
      const props: OpenInExplorerPanelProps = {
        databaseInfo,
        isPublishingRules,
        modelAliasInfo,
        modelsInfo,
        ruleInfo,
      };
      openPanel({ renderPanel: OpenInExplorerPanel, props });
      publish({
        ...props,
        databaseInfo,
        isConsolidatingModels,
        modelAliasInfo,
        projectId,
        spgInfo,
      });
    }
  }, [
    databaseInfo,
    hasValidMappings,
    isConsolidatingModels,
    isPublishingRules,
    isUsingAlias,
    localModelAliasInfo,
    modelsInfo,
    nextButtonError,
    openPanel,
    projectId,
    publish,
    ruleInfo,
    spgInfo,
  ]);

  return (
    <>
      <div
        className={Classes.DRAWER_BODY}
        data-testid={TEST_IDS.publishProjectDrawer.modelAlias.container}
      >
        <PublishProjectDrawerStepper active="model" />

        <div>
          <ModelAliasesForm
            databaseId={databaseId}
            existingAliasModel={existingAliasModel}
            isUsingAlias={isUsingAlias}
            modelAliasInfo={localModelAliasInfo}
            modelsInfo={modelsInfo}
            setExistingAliasModel={setExistingAliasModel}
            setIsUsingAlias={setIsUsingAlias}
            setModelAliasInfo={setLocalModelAliasInfo}
            validateModelName={(modelName) =>
              validateModelName({ ...localModelAliasInfo, modelName })
            }
          />
        </div>
      </div>

      <PublishProjectDrawerFooter
        handleBack={closePanel}
        handleNext={handleNext}
        nextError={nextButtonError}
        nextTestId={
          hasValidMappings
            ? TEST_IDS.publishProjectDrawer.footer.nextToMappingButton
            : TEST_IDS.publishProjectDrawer.footer.publishButton
        }
        nextText={
          hasValidMappings
            ? copy.components.publishProjectDrawer.footer.next
            : copy.components.publishProjectDrawer.footer.publish
        }
      />
    </>
  );
};
